// {{placeholder}} support for radiology report templates.
//
// A template author writes tokens like {{patient_name}} anywhere in the
// document; when the radiologist applies that template to an order, the tokens
// are replaced with the live patient/order values. This is the ONLY structure
// the frontend imposes on template content — everything else is opaque HTML.
//
// Same mustache convention as the discharge-summary templates
// (ipd/utils/templateHydrator.js), kept separate because radiology hydrates
// from an order, not from an admission summary.
import { formatDateOnly, formatDateTime } from "../radiologyOrdersShared";

// Shown in the template editor's "Insert placeholder" menu, in this order.
export const PLACEHOLDERS = [
  { token: "patient_name", label: "Patient name" },
  { token: "uhid", label: "UHID" },
  { token: "age", label: "Age" },
  { token: "gender", label: "Gender" },
  { token: "ward_bed", label: "Ward / bed" },
  { token: "order_no", label: "Order number" },
  { token: "investigation", label: "Investigation" },
  { token: "modality", label: "Modality" },
  { token: "priority", label: "Priority" },
  { token: "referring_doctor", label: "Referring doctor" },
  { token: "order_date", label: "Order date" },
  { token: "scheduled_date", label: "Scheduled date/time" },
  { token: "report_date", label: "Report date" },
  { token: "radiologist", label: "Radiologist" },
  { token: "facility_name", label: "Facility name" },
];

export function placeholderValues(patient = {}, order = {}, extra = {}) {
  return {
    patient_name: patient.name || "",
    uhid: patient.uhid || "",
    age: patient.age ? `${patient.age}` : "",
    gender: patient.gender || "",
    ward_bed: [patient.floor, patient.room, patient.bed].filter(Boolean).join(" · "),
    order_no: order.orderNo || "",
    investigation: order.service || "",
    modality: order.modality || "",
    priority: order.priority || "",
    referring_doctor: order.doctor || "",
    order_date: order.ordered ? formatDateOnly(order.ordered) : "",
    scheduled_date: order.scheduled ? formatDateTime(order.scheduled) : "",
    report_date: formatDateOnly(new Date().toISOString()),
    radiologist:
      extra.radiologistName || sessionStorage.getItem("doctor_name") || "",
    facility_name: sessionStorage.getItem("facility_name") || "",
    ...extra.values,
  };
}

// Unresolved tokens are left visible rather than blanked — a radiologist
// spotting "{{uhid}}" in the draft is far safer than a silently empty field.
export function hydratePlaceholders(html, values) {
  if (!html) return "";
  return String(html).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, token) => {
    const value = values?.[token];
    return value === undefined || value === null || value === "" ? match : String(value);
  });
}

export const unresolvedPlaceholders = (html) => {
  const found = new Set();
  String(html || "").replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, token) => {
    found.add(token);
    return m;
  });
  return [...found];
};

// Rough "is there anything in here" check — used to warn before overwriting
// typed content with a freshly applied template.
export const htmlIsEmpty = (html) =>
  !String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length;
